import { useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { useAppStore } from '../../hooks/useAppStore';
import {
  gameLandingContainerStyle,
  containerOverlayStyle,
  headerContainerStyle,
  headerGameLogoStyle
} from './GameLandingScreenStyles';
import GameLogo from './GameLogo';
import SponserDetails from './SponserDetails';
import TournamentLogo from './TournamentLogo';
import {
  quizContainerStyle,
  quizScreenBgStyle,
  sponserWrapperStyle,
  headerTournamentLogoStyle
} from './QuizScreenStyles';
import QuizWrapper from './QuizWrapper';
import {
  fetchTriviaQuestions,
  submitQuizAnswer
} from '../../services/apiRequests/TriviaService';
import QuizControls from './QuizControls';
import {
  GamificationCampaignStatus,
  GamificationScreens
} from '../../store/interface';
import {
  DEVICE_WIDTH_CONSTANTS,
  SOURCE_URL_TO_REDIRECT
} from '../../utils/constants/utilConstants';
import QuizResult from './QuizResult';
import {
  parseGameMapperId,
  triggerQuizCTAEvent,
  triggerQuizPageEvent
} from '../../utils/watchAndWinUtils';
import {
  MIXPANEL_CLOSE,
  MIXPANEL_NEXT_QUESTION,
  MIXPANEL_SUBMIT
} from '../../utils/analytics/mixpanel/mixpanelConstants';
import { DEVICE_TYPES } from '../../utils/constants/AppConstants';
import Spinner from '../Spinner';
import ToastMessage from '../toastMessage';
import { getSessionStorage } from '../../utils/browserStorageHandler';

const QuizScreen = () => {
  const {
    gamificationState = {},
    appConfig,
    isUserLoggedIn,
    setGamificationState
  } = useAppStore();
  const {
    config = null,
    questions = [],
    currentScreen,
    selectedAnswerId,
    campaignStatus,
    gameId
  } = gamificationState;
  const searchParams = useSearchParams();
  const gameMapperId = parseGameMapperId(
    searchParams.get('game_mapper_id') || ''
  );
  const settingsEntry = searchParams.get('settings_entry') || '';
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<string>('');
  const isLastQuestion = activeQuestionIndex === questions.length - 1;
  const backgroundImage =
    appConfig.deviceType === DEVICE_TYPES.DESKTOP
      ? config?.quizPageBackgroundDesktop
      : config?.quizPageBackground;

  const fetchQuestions = async () => {
    setLoading(true);
    const response = await fetchTriviaQuestions(gameMapperId as string);
    if (response) {
      const questions =
        response?.sequentialTriviaQuestions?.triviaQuestions || [];
      const campaignStatus =
        response?.sequentialTriviaQuestions?.campaignStatus;
      const gameId = response?.sequentialTriviaQuestions?.gameMetaData?.gameId;
      if (
        questions &&
        questions.length > 0 &&
        questions?.[0]?.userAnswerResponse
      ) {
        const hasCompletedQuiz =
          questions[questions?.length - 1]?.userAnswerResponse;
        // Set active page to results if he answered all questions
        if (hasCompletedQuiz) {
          setGamificationState({ currentScreen: GamificationScreens.Result });
        } else {
          const getLastAnsweredIndex = questions.findIndex(
            (item: any, index: number) =>
              item.userAnswerResponse &&
              !questions[index + 1].userAnswerResponse
          );
          // Show next question page if he has answered already and not completed quiz
          if (getLastAnsweredIndex >= 0) {
            setActiveQuestionIndex(getLastAnsweredIndex + 1);
          }
        }
      }

      if (
        settingsEntry === 'true' &&
        campaignStatus &&
        (campaignStatus === GamificationCampaignStatus.COMPLETED ||
          campaignStatus === GamificationCampaignStatus.SCHEDULED)
      ) {
        return setGamificationState({
          currentScreen: GamificationScreens.Winners
        });
      }

      if (campaignStatus) {
        setGamificationState({
          questions,
          campaignStatus,
          gameId
        });
      } else {
        setGamificationState({
          currentScreen: GamificationScreens.Winners
        });
      }
    }
    setLoading(false);
  };

  const onSubmitAnswerHandler = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const response = await submitQuizAnswer({
      questionId: questions[activeQuestionIndex]?.id,
      gameId: gameId as string,
      userSelectedOptionId: selectedAnswerId as string
    });

    if (response?.errors?.length) {
      setShowToast(true);
      setToastMsg(response?.errors?.[0]?.extensions?.error?.userDisplayMessage);
    } else {
      if (activeQuestionIndex < questions?.length) {
        setActiveQuestionIndex((prevIndex) => prevIndex + 1);
        setGamificationState({ selectedAnswerId: null });
        triggerQuizCTAEvent(
          gameMapperId as string,
          MIXPANEL_NEXT_QUESTION,
          questions[activeQuestionIndex]?.id,
          selectedAnswerId
        );
      }

      if (isLastQuestion) {
        triggerQuizCTAEvent(
          gameMapperId as string,
          MIXPANEL_SUBMIT,
          questions[activeQuestionIndex]?.id,
          selectedAnswerId
        );
        setGamificationState({
          currentScreen: GamificationScreens.Result,
          selectedAnswerId: null
        });
      }
    }
  };

  const onCloseQuizHandler = (e: React.SyntheticEvent) => {
    e.preventDefault();
    // Close quiz handler
    triggerQuizCTAEvent(
      gameMapperId as string,
      MIXPANEL_CLOSE,
      questions[activeQuestionIndex]?.id,
      selectedAnswerId
    );
    const sourceUrlToRedirect = getSessionStorage(SOURCE_URL_TO_REDIRECT);
    if (sourceUrlToRedirect) {
      window.location.href = sourceUrlToRedirect;
    } else {
      setGamificationState({
        currentScreen: GamificationScreens.Landing,
        questions: [],
        campaignStatus: GamificationCampaignStatus.IDLE,
        selectedAnswerId: null
      });
    }
  };

  useEffect(() => {
    if (isUserLoggedIn) {
      if (
        campaignStatus === GamificationCampaignStatus.SCHEDULED ||
        (campaignStatus === GamificationCampaignStatus.ACTIVE &&
          !questions.length) ||
        campaignStatus === GamificationCampaignStatus.COMPLETED
      ) {
        setGamificationState({ currentScreen: GamificationScreens.Result });
      }
    }
  }, [campaignStatus, isUserLoggedIn]);

  useEffect(() => {
    fetchQuestions();
    triggerQuizPageEvent(gameMapperId as string);
  }, []);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (showToast) {
      timeout = setTimeout(() => {
        setShowToast(false);
        setToastMsg('');
      }, 3000);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [showToast]);

  return (
    <section css={gameLandingContainerStyle}>
      <div
        css={[
          containerOverlayStyle,
          quizContainerStyle,
          backgroundImage && quizScreenBgStyle(backgroundImage)
        ]}>
        {loading ? (
          <Spinner />
        ) : (
          <>
            <div css={[headerContainerStyle]}>
              {config?.quizTitleImage && (
                <GameLogo extendedStyles={headerGameLogoStyle} />
              )}
              {(appConfig?.deviceWidth as number) >
                DEVICE_WIDTH_CONSTANTS.deviceWidth900 &&
                config?.sponsorLogo && (
                  <SponserDetails extendedStyles={sponserWrapperStyle} />
                )}
              {config?.bannerLogoImage && (
                <TournamentLogo extendedStyles={headerTournamentLogoStyle} />
              )}
            </div>
            {(appConfig?.deviceWidth as number) <
              DEVICE_WIDTH_CONSTANTS.deviceWidth900 &&
              config?.sponsorLogo && (
                <SponserDetails extendedStyles={sponserWrapperStyle} />
              )}
            {currentScreen == GamificationScreens.Result && <QuizResult />}

            {currentScreen == GamificationScreens.Quiz && (
              <>
                <QuizWrapper activeQuestionIndex={activeQuestionIndex} />
                <QuizControls
                  isBtnDisabled={!selectedAnswerId}
                  closeBtnHandler={onCloseQuizHandler}
                  submitBtnHandler={onSubmitAnswerHandler}
                  isLastQuestion={isLastQuestion}
                />
              </>
            )}
          </>
        )}
      </div>
      {showToast && <ToastMessage message={toastMsg} duration={3000} />}
    </section>
  );
};

export default QuizScreen;
